import { z } from "zod";

/** Browser-safe presentation contracts for governed report capabilities. */
import { GOVERNED_CAPABILITY_CONTRACTS, type GovernedCapabilityContract } from "./contracts";
import { financialColumnKeys } from "./financial-columns";
import { type GovernedInsightReportCapabilityKey, GovernedInsightReportCapabilityKeySchema } from "./identity";

export type { GovernedInsightReportCapabilityKey } from "./identity";
export { GovernedInsightReportCapabilityKeySchema } from "./identity";

const ViewPresetSchema = z
  .object({
    fieldKeys: z.array(z.string().min(1).max(64)).min(1).max(40),
    requiresComparison: z.boolean().optional(),
  })
  .strict();

/**
 * Record-mode reports whose dataset repeats one header across many evidence rows (an order and its
 * lines). The canvas renders the header as cards and these columns as a table beneath it, and the
 * CSV export carries both. `rowKindKey` names the field that says what a row is; rows equal to
 * `headerRowKind` are the header-only row an empty order publishes, so they never list as lines.
 */
const RecordLinePresetSchema = z
  .object({
    title: z.string().min(1).max(96),
    rowKindKey: z.string().min(1).max(64),
    headerRowKind: z.string().min(1).max(64),
    fieldKeys: z.array(z.string().min(1).max(64)).min(1).max(12),
  })
  .strict();

export const GovernedInsightPresentationManifestSchema = z
  .object({
    capabilityKey: GovernedInsightReportCapabilityKeySchema,
    datasetKey: z.string().min(1).max(64),
    preferredMode: z.enum(["table", "record", "metrics"]),
    titlePolicy: z.discriminatedUnion("kind", [
      z.object({ kind: z.literal("model") }).strict(),
      z.object({ kind: z.literal("fixed"), title: z.string().min(1).max(72) }).strict(),
      z.object({ kind: z.literal("template"), template: z.string().min(1).max(72) }).strict(),
    ]),
    defaultView: z.enum(["ranking", "detail", "comparison", "retention"]),
    viewPresets: z
      .object({
        ranking: ViewPresetSchema.optional(),
        detail: ViewPresetSchema.optional(),
        comparison: ViewPresetSchema.optional(),
        retention: ViewPresetSchema.optional(),
      })
      .strict(),
    recordLines: RecordLinePresetSchema.optional(),
    currencyLane: z
      .object({
        displayFieldKey: z.string(),
        modelCurrencyKeys: z.array(z.string()),
        populationCurrencyPaths: z.array(z.array(z.string())).min(1),
      })
      .strict()
      .optional(),
    allowedFieldKeys: z.array(z.string().min(1).max(64)).min(1).max(64),
    allowedSortFieldKeys: z.array(z.string().min(1).max(64)).max(64),
    rowInteraction: z
      .object({
        mode: z.literal("locked"),
        reason: z.literal("order_dependent_calculation"),
      })
      .strict()
      .optional(),
    identityLabels: z.array(z.string().min(1).max(96)).min(1).max(8),
    proseRowLimit: z.number().int().positive().max(50).optional(),
    fieldWidths: z.record(z.object({ minWidth: z.number().int().positive(), maxWidth: z.number().int().positive() }).strict()),
    fieldCompanions: z
      .array(
        z
          .object({
            whenFieldKey: z.string().min(1).max(64),
            companionFieldKeys: z.array(z.string().min(1).max(64)).min(1).max(4),
          })
          .strict(),
      )
      .max(4)
      .optional(),
    materialWarningCategories: z.array(z.string().min(1).max(64)).max(12),
    activity: z.object({ running: z.string().min(1).max(96), done: z.string().min(1).max(96) }).strict(),
    emptyState: z.object({ title: z.string().min(1).max(96), description: z.string().min(1).max(240) }).strict(),
    coverageCopy: z.object({ bounded: z.string().min(1).max(160), partial: z.string().min(1).max(160) }).strict(),
  })
  .strict()
  .superRefine((manifest, ctx) => {
    const allowed = new Set(manifest.allowedFieldKeys);
    for (const fieldKey of manifest.recordLines?.fieldKeys ?? []) {
      if (!allowed.has(fieldKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["recordLines", "fieldKeys"],
          message: "Record line columns must be allowed fields.",
        });
      }
    }
    for (const companion of manifest.fieldCompanions ?? []) {
      if (!allowed.has(companion.whenFieldKey) || companion.companionFieldKeys.some(fieldKey => !allowed.has(fieldKey))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["fieldCompanions"],
          message: "Companion columns must be allowed fields.",
        });
      }
    }
  });

export type GovernedInsightPresentationManifest = z.infer<typeof GovernedInsightPresentationManifestSchema>;

export const GOVERNED_INSIGHT_DEFAULT_PROSE_ROW_LIMIT = 5;

// Coverage copy may use {shown} for the rows on screen and {total} for the matching population.
// A sentence that asks for {total} falls back to a plain count when the total isn't known.
const reportCoverageCopy = {
  bounded: "Showing the first {shown} rows, up to this report's limit.",
  partial: "Only part of this result loaded. What's shown is correct.",
};

/**
 * Coverage copy for `find_inventory_pressure`.
 *
 * Named rather than inlined, following `reportCoverageCopy` above. Deliberately NOT that shared
 * constant: this capability excludes quarantined stock from its usable figures, the opposite of the
 * neighbouring `find_inventory_balances` copy, which states that quarantine counts on hand.
 *
 * `bounded` is wrapped across two source lines because it faces two independent caps — the schema's
 * `.max(160)` on the string, and eslint `max-len` 140 on the source line including indentation. At
 * 150 characters it clears the first and would breach the second if written inline.
 */
const reportEmptyState = {
  title: "Nothing matched",
  description: "No records matched what you asked for. Try a wider date range, or remove a filter.",
};

const financialManifest = (
  capabilityKey: GovernedInsightReportCapabilityKey,
  datasetKey: string,
  fields: string[],
  availableFields: string[],
): GovernedInsightPresentationManifest => ({
  capabilityKey,
  datasetKey,
  preferredMode: "table",
  titlePolicy: { kind: "model" },
  defaultView: "ranking",
  viewPresets: { ranking: { fieldKeys: fields }, detail: { fieldKeys: fields } },
  allowedFieldKeys: availableFields,
  allowedSortFieldKeys: availableFields,
  identityLabels: ["Counterparty", "Currency"],
  fieldWidths: { counterpartyName: { minWidth: 180, maxWidth: 320 } },
  materialWarningCategories: ["unsafe_display_value"],
  currencyLane: {
    displayFieldKey: "transactionCurrencyCode",
    modelCurrencyKeys: ["transactionCurrencyCode"],
    populationCurrencyPaths: [
      ["totals", "byTransactionCurrency", "transactionCurrencyCode"],
      ["rows", "transactionCurrencyCode"],
    ],
  },
  activity: { running: "Loading financial facts", done: "Loaded financial facts" },
  emptyState: reportEmptyState,
  coverageCopy: reportCoverageCopy,
});

const PRESENTATION_MANIFESTS_BY_CAPABILITY: Record<GovernedInsightReportCapabilityKey, GovernedInsightPresentationManifest> = {
  find_invoices: financialManifest(
    "find_invoices",
    "invoice-finder",
    ["documentNumber", "counterpartyName", "status", "openAmount", "dueDate", "daysPastDue"],
    financialColumnKeys("documents", "receivables"),
  ),
  find_bills: financialManifest(
    "find_bills",
    "bill-finder",
    ["documentNumber", "counterpartyName", "status", "openAmount", "dueDate", "daysPastDue"],
    financialColumnKeys("documents", "payables"),
  ),
  find_customer_payments: financialManifest(
    "find_customer_payments",
    "customer-payment-finder",
    ["paymentNumber", "counterpartyName", "cashAmount", "paidDate"],
    financialColumnKeys("payments", "receivables"),
  ),
  find_supplier_payments: financialManifest(
    "find_supplier_payments",
    "supplier-payment-finder",
    ["paymentNumber", "counterpartyName", "cashAmount", "paidDate"],
    financialColumnKeys("payments", "payables"),
  ),
  summarize_ap_aging: financialManifest(
    "summarize_ap_aging",
    "ap-aging",
    ["counterpartyName", "pastDueAmount", "creditAmount", "netAmount", "dueByAmount", "usualDaysLate"],
    financialColumnKeys("aging", "payables"),
  ),
  summarize_payments_received: financialManifest(
    "summarize_payments_received",
    "payments-received",
    ["counterpartyName", "month", "cashAmount", "appliedAmount", "totalAmount", "appliedCashNote"],
    financialColumnKeys("summary", "receivables"),
  ),
  summarize_payments_made: financialManifest(
    "summarize_payments_made",
    "payments-made",
    ["counterpartyName", "month", "cashAmount", "totalAmount"],
    financialColumnKeys("summary", "payables"),
  ),
  summarize_sales: {
    capabilityKey: "summarize_sales",
    datasetKey: "sales-summary",
    currencyLane: {
      displayFieldKey: "currencyCode",
      modelCurrencyKeys: ["currencyCode"],
      populationCurrencyPaths: [
        ["totals", "primary", "currencyCode"],
        ["basis", "currencyCode"],
      ],
    },
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: { fieldKeys: ["groupName", "group2Name", "salesAmount", "grossProfitAmount", "grossMarginPercentage"] },
      detail: {
        fieldKeys: [
          "groupName",
          "group2Name",
          "salesAmount",
          "grossProfitAmount",
          "grossMarginPercentage",
          "totalCostAmount",
          "quantity",
          "volumeLbs",
          "orderCount",
          "firstDate",
          "lastDate",
        ],
      },
      comparison: {
        fieldKeys: [
          "groupName",
          "group2Name",
          "salesAmount",
          "comparisonSalesAmount",
          "salesChangeAmount",
          "grossProfitAmount",
          "comparisonGrossProfitAmount",
          "grossMarginChangePoints",
        ],
        requiresComparison: true,
      },
      retention: {
        fieldKeys: ["groupName", "group2Name", "salesAmount", "comparisonSalesAmount", "salesChangeAmount", "comparisonStatus"],
        requiresComparison: true,
      },
    },
    allowedFieldKeys: [
      "groupName",
      "group2Name",
      "salesAmount",
      "grossProfitAmount",
      "grossMarginPercentage",
      "totalCostAmount",
      "quantity",
      "qtyUnit",
      "volumeLbs",
      "orderCount",
      "firstDate",
      "lastDate",
      "comparisonSalesAmount",
      "salesChangeAmount",
      "comparisonGrossProfitAmount",
      "grossMarginChangePoints",
      "comparisonStatus",
    ],
    allowedSortFieldKeys: ["salesAmount", "grossProfitAmount", "grossMarginPercentage", "salesChangeAmount", "groupName"],
    identityLabels: ["Group"],
    fieldWidths: { groupName: { minWidth: 180, maxWidth: 320 }, group2Name: { minWidth: 160, maxWidth: 280 } },
    materialWarningCategories: ["unsafe_display_value"],
    activity: { running: "Running sales summary", done: "Ran sales summary" },
    emptyState: reportEmptyState,
    coverageCopy: reportCoverageCopy,
  },
  summarize_product_demand_history: {
    capabilityKey: "summarize_product_demand_history",
    datasetKey: "product-demand-history",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "productCode",
          "customerName",
          "month",
          "orderCount",
          "orderGapMedianDays",
          "shipCount",
          "shipGapMedianDays",
          "shippedPounds",
          "shippedPackages",
          "completedYtdShippedPounds",
          "annualizedPacePounds",
          "paceStatus",
        ],
      },
    },
    allowedFieldKeys: [
      "productCode",
      "customerName",
      "month",
      "orderCount",
      "orderActiveDays",
      "orderActiveMonths",
      "orderIntervalCount",
      "orderFirstDate",
      "orderLastDate",
      "orderGapMedianDays",
      "orderGapMinDays",
      "orderGapMaxDays",
      "orderGapCv",
      "shipCount",
      "shipActiveDays",
      "shipActiveMonths",
      "shipIntervalCount",
      "shipFirstDate",
      "shipLastDate",
      "shipGapMedianDays",
      "shipGapMinDays",
      "shipGapMaxDays",
      "shipGapCv",
      "orderDateCoverage",
      "shipDateCoverage",
      "missingOrderDateCount",
      "missingShipDateCount",
      "shippedPounds",
      "shippedPackages",
      "packagingType",
      "packagingName",
      "demandScope",
      "completedYtdShippedPounds",
      "completedMonths",
      "annualizedPacePounds",
      "paceStatus",
    ],
    allowedSortFieldKeys: ["productCode", "customerName", "month", "orderCount", "shipCount", "shippedPackages", "annualizedPacePounds"],
    identityLabels: ["Product", "Customer", "Month"],
    fieldWidths: {
      productCode: { minWidth: 140, maxWidth: 260 },
      customerName: { minWidth: 180, maxWidth: 320 },
    },
    materialWarningCategories: ["history", "weight", "cadence"],
    activity: { running: "Calculating product demand history", done: "Calculated product demand history" },
    emptyState: { title: "No selected products", description: "No active selected products are available for this demand report." },
    coverageCopy: reportCoverageCopy,
  },
  summarize_purchases: {
    capabilityKey: "summarize_purchases",
    datasetKey: "purchase-summary",
    currencyLane: {
      displayFieldKey: "currencyCode",
      modelCurrencyKeys: ["currencyCode"],
      populationCurrencyPaths: [
        ["totals", "primary", "currencyCode"],
        ["basis", "currencyCode"],
      ],
    },
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: { fieldKeys: ["groupName", "purchaseSpendAmount", "orderCount"] },
      detail: { fieldKeys: ["groupName", "purchaseSpendAmount", "orderCount", "itemCount", "firstDate", "lastDate"] },
      comparison: {
        fieldKeys: ["groupName", "purchaseSpendAmount", "comparisonPurchaseSpendAmount", "purchaseSpendChangeAmount"],
        requiresComparison: true,
      },
      retention: {
        fieldKeys: ["groupName", "purchaseSpendAmount", "comparisonPurchaseSpendAmount", "purchaseSpendChangeAmount", "comparisonStatus"],
        requiresComparison: true,
      },
    },
    allowedFieldKeys: [
      "groupName",
      "purchaseSpendAmount",
      "orderCount",
      "itemCount",
      "firstDate",
      "lastDate",
      "comparisonPurchaseSpendAmount",
      "purchaseSpendChangeAmount",
      "comparisonStatus",
    ],
    allowedSortFieldKeys: ["purchaseSpendAmount", "purchaseSpendChangeAmount"],
    identityLabels: ["Group"],
    fieldWidths: { groupName: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: ["unsafe_display_value"],
    activity: { running: "Running purchase summary", done: "Ran purchase summary" },
    emptyState: reportEmptyState,
    coverageCopy: reportCoverageCopy,
  },
  summarize_open_sales_orders: {
    capabilityKey: "summarize_open_sales_orders",
    datasetKey: "open-sales-orders",
    currencyLane: {
      displayFieldKey: "currencyCode",
      modelCurrencyKeys: ["currencyCode"],
      populationCurrencyPaths: [["totals", "currencyCode"]],
    },
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: { fieldKeys: ["groupName", "openSalesOrderAmount", "orderCount"] },
      detail: { fieldKeys: ["groupName", "openSalesOrderAmount", "orderCount", "itemCount"] },
    },
    allowedFieldKeys: ["groupName", "openSalesOrderAmount", "orderCount", "itemCount"],
    allowedSortFieldKeys: ["openSalesOrderAmount", "orderCount"],
    identityLabels: ["Group"],
    fieldWidths: { groupName: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: ["unsafe_display_value"],
    activity: { running: "Running open sales orders", done: "Ran open sales orders" },
    emptyState: reportEmptyState,
    coverageCopy: reportCoverageCopy,
  },
  summarize_ar_aging: financialManifest(
    "summarize_ar_aging",
    "ar-aging",
    [
      "counterpartyName",
      "pastDueAmount",
      "creditAmount",
      "netAmount",
      "days1To30Amount",
      "days31To60Amount",
      "days61To90Amount",
      "days90PlusAmount",
      "usualDaysLate",
      "olderDueOpenAmount",
      "olderDuePaidInWindowAmount",
    ],
    financialColumnKeys("aging", "receivables"),
  ),
  summarize_customer_invoice_settlement_behavior: {
    capabilityKey: "summarize_customer_invoice_settlement_behavior",
    datasetKey: "customer-invoice-settlement-behavior",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: {
        fieldKeys: [
          "customerName",
          "lastPaymentDate",
          "usualDaysLate",
          "averageDaysToPay",
          "olderDueOpenAmount",
          "olderDuePaidInWindowAmount",
        ],
      },
      detail: {
        fieldKeys: [
          "invoiceNumber",
          "customerName",
          "invoiceDay",
          "dueDay",
          "finalSettlementDay",
          "dateProvenance",
          "daysToFinalSettlement",
          "daysVersusDue",
          "baseAmount",
          "writeOffBaseAmount",
        ],
      },
    },
    allowedFieldKeys: [
      "counterpartyStatus",
      "lastPaymentDate",
      "usualDaysLate",
      "recentDaysLate",
      "averageDaysToPay",
      "nativePaidDocumentCount",
      "settledOtherwiseCount",
      "olderDueOpenAmount",
      "olderDuePaidInWindowAmount",
      "olderDueCreditInWindowAmount",
      "olderDueAdjustmentInWindowAmount",

      "invoiceNumber",
      "customerName",
      "invoiceDay",
      "dueDay",
      "finalSettlementDay",
      "dateProvenance",
      "daysToFinalSettlement",
      "daysVersusDue",
      "transactionAmount",
      "baseAmount",
      "writeOffBaseAmount",
      "cohort",
    ],
    allowedSortFieldKeys: ["finalSettlementDay", "daysToFinalSettlement", "daysVersusDue", "baseAmount"],
    identityLabels: ["Customer", "Invoice"],
    fieldWidths: { invoiceNumber: { minWidth: 120, maxWidth: 200 }, customerName: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: ["incomplete_population", "incoherent_source_record", "unsafe_display_value"],
    activity: { running: "Checking invoice payment behavior", done: "Checked invoice payment behavior" },
    emptyState: { title: "No settled invoices", description: "No paid invoices had a recorded final settlement day in this window." },
    coverageCopy: {
      bounded: "Settlement totals cover the full population; showing {shown} of {total} evidence rows.",
      partial: "Some timing or base-currency measures are withheld because required source evidence is incomplete.",
    },
  },
  summarize_customer_credit_exposure: {
    capabilityKey: "summarize_customer_credit_exposure",
    datasetKey: "customer-credit-exposure",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: {
        fieldKeys: [
          "customerName",
          "netPostedExposure",
          "committedUninvoicedExposure",
          "totalExposure",
          "creditLimit",
          "availableCredit",
          "flags",
        ],
      },
      detail: {
        fieldKeys: [
          "customerName",
          "grossOpenInvoices",
          "openCredits",
          "netPostedExposure",
          "confirmedCommitment",
          "shippedCommitment",
          "deliveredCommitment",
          "committedUninvoicedExposure",
          "totalExposure",
          "creditLimit",
          "availableCredit",
          "flags",
        ],
      },
    },
    allowedFieldKeys: [
      "counterpartyStatus",
      "customerName",
      "grossOpenInvoices",
      "openCredits",
      "netPostedExposure",
      "confirmedCommitment",
      "shippedCommitment",
      "deliveredCommitment",
      "committedUninvoicedExposure",
      "totalExposure",
      "creditLimit",
      "availableCredit",
      "utilizationPercent",
      "overage",
      "flags",
    ],
    allowedSortFieldKeys: ["totalExposure", "netPostedExposure", "committedUninvoicedExposure", "customerName"],
    identityLabels: ["Customer"],
    fieldWidths: { customerName: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: ["incomplete_population", "unsafe_display_value"],
    activity: { running: "Checking customer credit exposure", done: "Checked customer credit exposure" },
    emptyState: { title: "No customer exposure", description: "No authorized customers matched this exposure request." },
    coverageCopy: {
      bounded: "Showing {shown} of {total} customer exposure rows.",
      partial: "Some exposure or limit-derived figures are withheld because required base or attribution evidence is incomplete.",
    },
  },
  summarize_customer_invoice_charges: {
    capabilityKey: "summarize_customer_invoice_charges",
    datasetKey: "customer-invoice-charges",
    preferredMode: "metrics",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "documentNumber",
          "customerName",
          "postedDay",
          "description",
          "ledgerAccount",
          "matchReason",
          "transactionAmount",
          "baseAmount",
        ],
      },
    },
    allowedFieldKeys: [
      "counterpartyStatus",
      "documentNumber",
      "customerName",
      "postedDay",
      "description",
      "ledgerAccount",
      "costCategory",
      "matchReason",
      "transactionAmount",
      "baseAmount",
      "kind",
    ],
    allowedSortFieldKeys: ["postedDay", "baseAmount", "customerName", "documentNumber"],
    identityLabels: ["Invoice or credit line"],
    fieldWidths: {
      documentNumber: { minWidth: 120, maxWidth: 200 },
      customerName: { minWidth: 180, maxWidth: 320 },
      description: { minWidth: 200, maxWidth: 420 },
    },
    materialWarningCategories: ["incomplete_population", "unsafe_display_value"],
    activity: { running: "Checking customer invoice charges", done: "Checked customer invoice charges" },
    emptyState: {
      title: "No matching charges",
      description: "No posted invoice lines or independently matching credits matched this request.",
    },
    coverageCopy: {
      bounded: "Charge totals cover the full population; showing {shown} of {total} matched evidence rows.",
      partial: "Some base-currency charge totals are withheld because required conversion evidence is incomplete.",
    },
  },
  summarize_customer_ar_position: {
    capabilityKey: "summarize_customer_ar_position",
    datasetKey: "customer-ar-position",
    preferredMode: "metrics",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "customerName",
          "grossOpenInvoices",
          "netPostedExposure",
          "committedExposure",
          "totalExposure",
          "creditLimit",
          "availableCredit",
          "pastDue",
          "days90Plus",
          "weightedDaysVersusDue",
          "flags",
          "coverageIssues",
        ],
      },
    },
    allowedFieldKeys: [
      "counterpartyStatus",
      "customerName",
      "grossOpenInvoices",
      "openCredits",
      "netPostedExposure",
      "committedExposure",
      "totalExposure",
      "creditLimit",
      "availableCredit",
      "pastDue",
      "days90Plus",
      "settledInvoiceCount",
      "weightedDaysVersusDue",
      "flags",
      "coverageIssues",
    ],
    allowedSortFieldKeys: [],
    identityLabels: ["Customer"],
    fieldWidths: { customerName: { minWidth: 180, maxWidth: 320 }, coverageIssues: { minWidth: 240, maxWidth: 520 } },
    materialWarningCategories: ["incomplete_population", "unsafe_display_value"],
    activity: { running: "Building customer AR position", done: "Built customer AR position" },
    emptyState: { title: "Customer AR position unavailable", description: "No authorized customer AR facts matched this request." },
    coverageCopy: {
      bounded: "Showing the bounded factual customer AR position.",
      partial: "The customer AR position is not presented because at least one financial component has incomplete evidence.",
    },
  },
  summarize_pipeline: {
    capabilityKey: "summarize_pipeline",
    datasetKey: "pipeline-summary",
    currencyLane: {
      displayFieldKey: "currencyCode",
      modelCurrencyKeys: ["currencyCode"],
      populationCurrencyPaths: [["basis", "currencyCode"]],
    },
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: { fieldKeys: ["groupName", "opportunityCount", "estimatedValueTotal", "noValueCount"] },
      detail: {
        fieldKeys: [
          "groupName",
          "opportunityCount",
          "estimatedValueTotal",
          "currencyCode",
          "noValueCount",
          "wonCount",
          "lostCount",
          "oldestOpenCreatedAt",
        ],
      },
    },
    allowedFieldKeys: [
      "groupName",
      "opportunityCount",
      "estimatedValueTotal",
      "currencyCode",
      "noValueCount",
      "wonCount",
      "lostCount",
      "oldestOpenCreatedAt",
    ],
    allowedSortFieldKeys: ["opportunityCount", "estimatedValueTotal", "noValueCount", "wonCount", "lostCount"],
    identityLabels: ["Group"],
    fieldWidths: { groupName: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: ["incomplete_population"],
    activity: { running: "Summarizing opportunity pipeline", done: "Summarized opportunity pipeline" },
    emptyState: { title: "No opportunities", description: "No authorized opportunities matched these pipeline filters." },
    coverageCopy: {
      bounded: "Showing {shown} of {total} opportunity pipeline groups.",
      partial: "The complete opportunity pipeline could not be loaded. Narrow the filters and retry.",
    },
  },
  search_opportunities: {
    capabilityKey: "search_opportunities",
    datasetKey: "opportunities",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: { fieldKeys: ["title", "status", "counterparty", "assignedTo", "estimatedValue", "updatedAt"] },
      detail: {
        fieldKeys: ["title", "status", "successStatus", "counterparty", "assignedTo", "estimatedValue", "createdAt", "updatedAt"],
      },
    },
    allowedFieldKeys: [
      "title",
      "status",
      "successStatus",
      "counterparty",
      "assignedTo",
      "estimatedValue",
      "currencyCode",
      "createdAt",
      "createdBy",
      "updatedAt",
    ],
    allowedSortFieldKeys: ["title", "status", "estimatedValue", "createdAt", "updatedAt"],
    identityLabels: ["Opportunity"],
    fieldWidths: { title: { minWidth: 200, maxWidth: 360 }, counterparty: { minWidth: 160, maxWidth: 280 } },
    materialWarningCategories: ["incomplete_population"],
    activity: { running: "Searching opportunities", done: "Searched opportunities" },
    emptyState: { title: "No opportunities", description: "No authorized opportunities matched these filters." },
    coverageCopy: {
      bounded: "Showing {shown} of {total} matching opportunities.",
      partial: "The complete opportunity population could not be loaded. Narrow the filters and retry.",
    },
  },
  get_opportunity_detail: {
    capabilityKey: "get_opportunity_detail",
    datasetKey: "opportunity-detail",
    preferredMode: "record",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "title",
          "status",
          "successStatus",
          "counterparty",
          "assignedTo",
          "estimatedValue",
          "description",
          "closingNotes",
          "createdAt",
          "updatedAt",
        ],
      },
    },
    allowedFieldKeys: [
      "title",
      "status",
      "successStatus",
      "counterparty",
      "assignedTo",
      "estimatedValue",
      "currencyCode",
      "description",
      "closingNotes",
      "products",
      "meta",
      "timeline",
      "statusIntegrity",
      "createdAt",
      "createdBy",
      "updatedAt",
    ],
    allowedSortFieldKeys: [],
    identityLabels: ["Opportunity"],
    fieldWidths: { title: { minWidth: 220, maxWidth: 420 } },
    materialWarningCategories: ["bounded_timeline"],
    activity: { running: "Loading opportunity detail", done: "Loaded opportunity detail" },
    emptyState: { title: "Opportunity not found", description: "No authorized opportunity matched this identifier." },
    coverageCopy: {
      bounded: "The opportunity is complete, with the newest 100 timeline events shown.",
      partial: "The opportunity detail could not be loaded completely.",
    },
  },
  search_cost_book_entries: {
    capabilityKey: "search_cost_book_entries",
    datasetKey: "cost_book_entries",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Cost Book Entries" },
    defaultView: "ranking",
    viewPresets: {
      ranking: {
        fieldKeys: [
          "product",
          "effectiveFrom",
          "effectiveTo",
          "entryType",
          "costBasis",
          "totalCostPerBaseUom",
          "rateSchedule",
          "uom",
          "currency",
          "supplier",
          "shipFrom",
        ],
      },
      detail: {
        fieldKeys: [
          "product",
          "supplier",
          "shipFrom",
          "incoterm",
          "entryType",
          "costBasis",
          "effectiveFrom",
          "effectiveTo",
          "totalCostPerBaseUom",
          "rateSchedule",
          "uom",
          "currency",
        ],
      },
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "product",
      "supplier",
      "shipFrom",
      "incoterm",
      "entryType",
      "costBasis",
      "claimRole",
      "effectiveFrom",
      "effectiveTo",
      "startingCostPerBaseUom",
      "additionalCostPerBaseUom",
      "totalCostPerBaseUom",
      "rateSchedule",
      "uom",
      "currency",
    ],
    allowedSortFieldKeys: ["product", "supplier", "effectiveFrom", "totalCostPerBaseUom"],
    identityLabels: ["Product", "Supplier", "Ship From"],
    fieldWidths: {
      product: { minWidth: 170, maxWidth: 260 },
      supplier: { minWidth: 150, maxWidth: 240 },
      costBasis: { minWidth: 190, maxWidth: 270 },
    },
    materialWarningCategories: [
      "excluded_unresolved_construction",
      "missing_starting_cost_evidence",
      "missing_starting_cost_uom",
      "unsupported_starting_cost_uom",
      "starting_cost_conversion_unavailable",
      "missing_target_package_quantity",
      "invalid_persisted_component",
      "unresolved_current_lane",
      "unresolved_current_population",
      "matched_face_filtered",
      "page_contains_unresolved_entries",
      "ambiguous_current_slot",
    ],
    activity: { running: "Searching Cost Book", done: "Searched Cost Book" },
    emptyState: { title: "No Cost Book entries", description: "No Cost Book entries matched this request." },
    coverageCopy: {
      bounded: "Showing the first {shown} matching Cost Book entries.",
      partial: "We couldn't load the full list. The entries shown are correct.",
    },
  },
  search_price_book_entries: {
    capabilityKey: "search_price_book_entries",
    datasetKey: "price_book_entries",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Price Book Entries" },
    defaultView: "ranking",
    viewPresets: {
      ranking: {
        fieldKeys: ["product", "customer", "normalizedPricePerBaseUom", "baseUom", "currency", "effectiveFrom", "effectiveTo"],
      },
      detail: {
        fieldKeys: [
          "product",
          "customer",
          "enteredPrice",
          "priceBasis",
          "normalizedPricePerBaseUom",
          "baseUom",
          "currency",
          "effectiveState",
          "overlapState",
          "effectiveFrom",
          "effectiveTo",
        ],
      },
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "product",
      "customer",
      "enteredPrice",
      "priceBasis",
      "normalizedPricePerBaseUom",
      "baseUom",
      "currency",
      "effectiveState",
      "overlapState",
      "effectiveFrom",
      "effectiveTo",
    ],
    allowedSortFieldKeys: ["product", "customer", "normalizedPricePerBaseUom", "effectiveFrom"],
    identityLabels: ["Product", "Customer"],
    fieldWidths: {
      product: { minWidth: 160, maxWidth: 240 },
      customer: { minWidth: 160, maxWidth: 260 },
      normalizedPricePerBaseUom: { minWidth: 190, maxWidth: 280 },
    },
    materialWarningCategories: [
      "multiple_active_claims",
      "invalid_date_window",
      "historical_basis_not_snapshotted",
      "package_meta_basis_conflict",
      "missing_reference_packaging",
      "invalid_reference_packaging",
      "reference_packaging_uom_mismatch",
    ],
    activity: { running: "Searching Price Book", done: "Searched Price Book" },
    emptyState: { title: "No Price Book entries", description: "No authorized Price Book entries matched this request." },
    coverageCopy: {
      bounded: "Showing the first {shown} matching Price Book entries.",
      partial: "We couldn't load the complete Price Book population, so comparisons are unavailable.",
    },
  },
  get_supplier_history: {
    capabilityKey: "get_supplier_history",
    datasetKey: "supplier_history",
    preferredMode: "record",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: { ranking: { fieldKeys: ["productCode", "supplierName"] }, detail: { fieldKeys: ["productCode", "supplierName"] } },
    allowedFieldKeys: ["productCode", "productId", "supplierName", "supplierId"],
    allowedSortFieldKeys: [],
    identityLabels: ["Product", "Supplier"],
    fieldWidths: {},
    materialWarningCategories: ["history_incomplete"],
    activity: { running: "Reading supplier history", done: "Read supplier history" },
    emptyState: { title: "No supplier history", description: "No products are set up or bought from this supplier yet." },
    coverageCopy: {
      bounded: "Narrow the supplier history by printed names or products.",
      partial: "Some supplier history is unavailable.",
    },
  },
  get_product_cost_context: {
    capabilityKey: "get_product_cost_context",
    datasetKey: "product_cost_context",
    preferredMode: "record",
    titlePolicy: { kind: "template", template: "Cost Context: {product}" },
    defaultView: "detail",
    viewPresets: {
      ranking: {
        fieldKeys: [
          "productCode",
          "supplierName",
          "currentCostPerBaseUom",
          "currentComparisonBasis",
          "priorCostPerBaseUom",
          "completedMaterialCostPerBaseUom",
          "completedLandedCostPerBaseUom",
          "pendingMaterialCostPerBaseUom",
          "baseUom",
          "currencyCode",
          "costBookAsOf",
          "evidenceObservedAt",
        ],
      },
      detail: {
        fieldKeys: [
          "productCode",
          "supplierName",
          "costBookAsOf",
          "currentCostPerBaseUom",
          "currentComparisonBasis",
          "priorCostPerBaseUom",
          "completedMaterialCostPerBaseUom",
          "completedEvidenceSourceDate",
          "pendingMaterialCostPerBaseUom",
          "pendingEvidenceSourceDate",
          "baseUom",
          "currencyCode",
        ],
      },
    },
    allowedFieldKeys: [
      "productCode",
      "supplierName",
      "costBookAsOf",
      "currentCostPerBaseUom",
      "currentComparisonBasis",
      "priorCostPerBaseUom",
      "completedMaterialCostPerBaseUom",
      "completedLandedCostPerBaseUom",
      "completedEvidenceSourceDate",
      "pendingMaterialCostPerBaseUom",
      "pendingEvidenceSourceDate",
      "baseUom",
      "currencyCode",
      "evidenceObservedAt",
    ],
    allowedSortFieldKeys: [],
    identityLabels: ["Product", "Supplier"],
    fieldWidths: {},
    materialWarningCategories: ["completed_purchase_missing", "on_order_purchase_missing", "unresolved_current_lane"],
    activity: { running: "Checking product cost context", done: "Checked product cost context" },
    emptyState: { title: "No cost context", description: "We don't have cost context for this product and supplier yet." },
    coverageCopy: { bounded: "Showing the cost context we have.", partial: "Some of the cost evidence isn't available right now." },
  },
  search_note_activity: {
    capabilityKey: "search_note_activity",
    datasetKey: "note-activity",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: { fieldKeys: ["dimensionLabel", "noteCount"] },
      detail: { fieldKeys: ["happenedDay", "noteType", "title", "author", "subjectType", "subjectName", "excerpt"] },
    },
    allowedFieldKeys: [
      "dimensionLabel",
      "noteCount",
      "happenedDay",
      "createdAt",
      "noteType",
      "title",
      "author",
      "subjectType",
      "subjectName",
      "excerpt",
    ],
    allowedSortFieldKeys: ["noteCount", "happenedDay", "noteType", "author", "subjectType", "subjectName"],
    identityLabels: ["Activity"],
    fieldWidths: {
      dimensionLabel: { minWidth: 180, maxWidth: 320 },
      title: { minWidth: 180, maxWidth: 320 },
      author: { minWidth: 140, maxWidth: 240 },
      subjectName: { minWidth: 160, maxWidth: 280 },
      excerpt: { minWidth: 260, maxWidth: 520 },
    },
    materialWarningCategories: ["subject_unresolved"],
    activity: { running: "Counting your notes", done: "Counted your notes" },
    emptyState: { title: "No notes in this period.", description: "Try a wider date range, or remove a filter to see more activity." },
    coverageCopy: {
      bounded: "Showing the largest groups. The counts still include every note.",
      partial: "We couldn't load every note. The counts shown may be incomplete.",
    },
  },
  search_email_activity: {
    capabilityKey: "search_email_activity",
    datasetKey: "email-activity",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    viewPresets: {
      ranking: { fieldKeys: ["dimensionLabel", "sentCount", "noteCount"] },
      detail: { fieldKeys: ["dimensionLabel", "subjectName", "sentCount", "noteCount"] },
    },
    allowedFieldKeys: ["dimensionLabel", "sentCount", "noteCount", "subjectName", "subjectId", "subjectType", "hasOtherNote"],
    allowedSortFieldKeys: ["sentCount", "noteCount", "dimensionLabel", "subjectName"],
    identityLabels: ["Email activity"],
    fieldWidths: {
      dimensionLabel: { minWidth: 180, maxWidth: 320 },
      subjectName: { minWidth: 160, maxWidth: 280 },
    },
    materialWarningCategories: [],
    activity: { running: "Counting emails and email notes", done: "Counted emails and email notes" },
    emptyState: { title: "No emails in this period.", description: "Try a wider date range, or remove a filter." },
    coverageCopy: {
      bounded: "Showing the rows that fit. The counts still include every email you can open.",
      partial: "Some emails could not be checked. The counts shown may be incomplete.",
    },
  },
  get_sent_email: {
    capabilityKey: "get_sent_email",
    datasetKey: "sent-email",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["kindLabel", "subject", "senderName", "linkedLabel"] } },
    allowedFieldKeys: ["kindLabel", "subject", "senderName", "linkedLabel", "linkedId", "linkedKind"],
    allowedSortFieldKeys: [],
    identityLabels: ["Sent email"],
    fieldWidths: {
      subject: { minWidth: 180, maxWidth: 360 },
      linkedLabel: { minWidth: 140, maxWidth: 240 },
    },
    materialWarningCategories: [],
    activity: { running: "Reading the sent email", done: "Read the sent email" },
    emptyState: { title: "Email not found.", description: "That sent email is not available." },
    coverageCopy: {
      bounded: "This is the email ShelfCycle handed to the mail provider.",
      partial: "Part of this email could not be read.",
    },
  },
  find_customer_accounts: {
    capabilityKey: "find_customer_accounts",
    datasetKey: "customer-accounts",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["name", "pipelineStatus", "commercialTermsState", "primaryOwner"] } },
    allowedFieldKeys: [
      "name",
      "customerId",
      "externalId",
      "pipelineStatus",
      "commercialTermsState",
      "owners",
      "primaryOwner",
      "paymentTerms",
      "customerServiceRepresentative",
      "accountManager",
      "locations",
      "locationTotal",
      "locationReturned",
      "locationsTruncated",
      "locationReadbackPath",
      "updatedAt",
      "createdBy",
      "createdAt",
      "archived",
    ],
    allowedSortFieldKeys: ["name", "pipelineStatus", "commercialTermsState"],
    identityLabels: ["Customer"],
    fieldWidths: { name: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: ["geography_projection_bounded"],
    activity: { running: "Finding customer accounts", done: "Found customer accounts" },
    emptyState: reportEmptyState,
    coverageCopy: reportCoverageCopy,
  },
  find_suppliers: {
    capabilityKey: "find_suppliers",
    datasetKey: "supplier-directory",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["name", "externalId", "email", "phone", "city", "state"] } },
    allowedFieldKeys: ["name", "supplierId", "externalId", "email", "phone", "city", "state", "createdBy", "createdAt", "archived"],
    allowedSortFieldKeys: ["name", "createdAt"],
    identityLabels: ["Supplier"],
    fieldWidths: { name: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: [],
    activity: { running: "Finding suppliers", done: "Found suppliers" },
    emptyState: reportEmptyState,
    coverageCopy: reportCoverageCopy,
  },
  list_warehouses: {
    capabilityKey: "list_warehouses",
    datasetKey: "warehouse-directory",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Warehouses" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["name", "externalId", "city", "state", "archivedAt"] } },
    allowedFieldKeys: [
      "id",
      "name",
      "externalId",
      "street1",
      "street2",
      "city",
      "country",
      "state",
      "zip",
      "phone",
      "email",
      "contactName",
      "directShip",
      "archivedAt",
      "updatedAt",
    ],
    allowedSortFieldKeys: ["name", "city", "state"],
    identityLabels: ["Warehouse"],
    fieldWidths: { name: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: [],
    activity: { running: "Listing warehouses", done: "Listed warehouses" },
    emptyState: { title: "No warehouses", description: "No physical warehouses match this directory request." },
    coverageCopy: reportCoverageCopy,
  },
  list_representatives: {
    capabilityKey: "list_representatives",
    datasetKey: "representative-directory",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Representatives" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["name", "kind", "externalId", "active", "email"] } },
    allowedFieldKeys: [
      "name",
      "id",
      "kind",
      "firstName",
      "lastName",
      "externalId",
      "active",
      "archivedAt",
      "updatedAt",
      "linkedUserId",
      "email",
      "phone",
      "emailProvenance",
    ],
    allowedSortFieldKeys: ["name", "kind", "active"],
    identityLabels: ["Representative"],
    fieldWidths: { name: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: [],
    activity: { running: "Listing representatives", done: "Listed representatives" },
    emptyState: { title: "No representatives", description: "No authorized representatives match this request." },
    coverageCopy: reportCoverageCopy,
  },
  list_payment_terms: {
    capabilityKey: "list_payment_terms",
    datasetKey: "payment-terms-directory",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Payment Terms" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["name", "description", "netDays", "discountDays", "discountPercentage"] } },
    allowedFieldKeys: ["name", "id", "description", "externalId", "netDays", "discountDays", "discountPercentage", "updatedAt"],
    allowedSortFieldKeys: ["name", "netDays", "discountDays"],
    identityLabels: ["Payment term"],
    fieldWidths: { name: { minWidth: 160, maxWidth: 280 }, description: { minWidth: 200, maxWidth: 420 } },
    materialWarningCategories: [],
    activity: { running: "Listing payment terms", done: "Listed payment terms" },
    emptyState: { title: "No payment terms", description: "No authorized payment terms match this request." },
    coverageCopy: reportCoverageCopy,
  },
  list_ledger_accounts: {
    capabilityKey: "list_ledger_accounts",
    datasetKey: "ledger-account-directory",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Ledger Accounts" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["name", "accountType", "externalId", "fragmentPath"] } },
    allowedFieldKeys: [
      "name",
      "id",
      "accountType",
      "externalId",
      "fragmentPath",
      "excludeFromReports",
      "isCogs",
      "isNonOperational",
      "updatedAt",
    ],
    allowedSortFieldKeys: ["name", "accountType", "fragmentPath"],
    identityLabels: ["Ledger account"],
    fieldWidths: { name: { minWidth: 180, maxWidth: 320 }, fragmentPath: { minWidth: 180, maxWidth: 360 } },
    materialWarningCategories: [],
    activity: { running: "Listing ledger accounts", done: "Listed ledger accounts" },
    emptyState: { title: "No ledger accounts", description: "No authorized ledger accounts match this request." },
    coverageCopy: reportCoverageCopy,
  },
  get_customer_contacts: {
    capabilityKey: "get_customer_contacts",
    datasetKey: "customer-contacts",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["name", "customerName", "title", "email", "officeNumber", "mobileNumber"] } },
    allowedFieldKeys: [
      "name",
      "contactId",
      "customerName",
      "customerId",
      "title",
      "email",
      "officeNumber",
      "mobileNumber",
      "isEmergency",
      "archived",
    ],
    allowedSortFieldKeys: ["name", "customerName", "title"],
    identityLabels: ["Contact"],
    fieldWidths: { name: { minWidth: 180, maxWidth: 300 }, customerName: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: [],
    activity: { running: "Loading customer contacts", done: "Loaded customer contacts" },
    emptyState: { title: "No contacts found", description: "No contacts matched the requested customers and archive setting." },
    coverageCopy: reportCoverageCopy,
  },
  get_supplier_contacts: {
    capabilityKey: "get_supplier_contacts",
    datasetKey: "supplier-contacts",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["name", "supplierName", "title", "email", "phone"] } },
    allowedFieldKeys: ["name", "contactId", "supplierName", "supplierId", "title", "email", "phone", "archived"],
    allowedSortFieldKeys: ["name", "supplierName", "title"],
    identityLabels: ["Contact"],
    fieldWidths: { name: { minWidth: 180, maxWidth: 300 }, supplierName: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: [],
    activity: { running: "Loading supplier contacts", done: "Loaded supplier contacts" },
    emptyState: { title: "No contacts found", description: "No contacts matched the requested supplier and archive setting." },
    coverageCopy: reportCoverageCopy,
  },
  find_customer_product_evidence: {
    capabilityKey: "find_customer_product_evidence",
    datasetKey: "customer-product-evidence",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "ranking",
    // Founder decision 2026-08-12: the grid is transactional customer-by-currency rows only.
    // Team notes and leads carry no amounts; they are presented in prose, never as money columns.
    viewPresets: {
      ranking: {
        fieldKeys: ["customerName", "linkedOrdersAmount", "invoicedMentionsAmount", "totalAmount"],
      },
    },
    allowedFieldKeys: ["customerName", "customerId", "linkedOrdersAmount", "invoicedMentionsAmount", "totalAmount", "currencyCode"],
    allowedSortFieldKeys: ["totalAmount", "customerName", "linkedOrdersAmount", "invoicedMentionsAmount"],
    identityLabels: ["Customer"],
    proseRowLimit: GOVERNED_INSIGHT_DEFAULT_PROSE_ROW_LIMIT,
    fieldWidths: {
      customerName: { minWidth: 180, maxWidth: 320 },
      linkedOrdersAmount: { minWidth: 120, maxWidth: 180 },
      invoicedMentionsAmount: { minWidth: 120, maxWidth: 180 },
      totalAmount: { minWidth: 120, maxWidth: 180 },
    },
    materialWarningCategories: [],
    activity: { running: "Checking customer-product evidence", done: "Checked customer-product evidence" },
    emptyState: {
      title: "No matching customers",
      description: "No authorized customers had evidence around that subject in the sources that were searched.",
    },
    coverageCopy: {
      // A truncated money search has no authoritative population: say what was proven, never a total.
      bounded: "Showing the {shown} rows the search could prove; its limit may hide more.",
      partial: reportCoverageCopy.partial,
    },
  },
  find_orders: {
    capabilityKey: "find_orders",
    datasetKey: "order-finder",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "orderNo",
          "customer",
          "shipTo",
          "product",
          "packaging",
          "acceptedQuantity",
          "quantityUom",
          "requiredBy",
          "shipBy",
          "effectiveDate",
          "commitmentBucket",
          "executionChannel",
          "counterparty",
          "status",
          "orderType",
          "salesPerson",
          "businessDate",
          "chemicalLines",
          "lineCoverage",
          "totalAmount",
        ],
      },
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "orderNo",
      "counterparty",
      "status",
      "orderType",
      "salesPerson",
      "businessDate",
      "shipOrDueDate",
      "chemicalLines",
      "lineCoverage",
      "totalAmount",
      "linkedOrderNo",
      "updatedAt",
      "commitmentLineId",
      "customer",
      "customerId",
      "shipTo",
      "shipToId",
      "product",
      "productId",
      "packaging",
      "acceptedQuantity",
      "quantityBasis",
      "quantityUom",
      "requiredBy",
      "shipBy",
      "effectiveDate",
      "scheduleBasis",
      "commitmentBucket",
      "executionChannel",
      "executionBasis",
    ],
    allowedSortFieldKeys: ["businessDate", "shipOrDueDate", "totalAmount", "updatedAt", "effectiveDate", "commitmentBucket"],
    identityLabels: ["Order"],
    fieldWidths: {
      orderNo: { minWidth: 140, maxWidth: 220 },
      counterparty: { minWidth: 180, maxWidth: 320 },
      status: { minWidth: 120, maxWidth: 190 },
    },
    materialWarningCategories: ["bounded_order_population"],
    activity: { running: "Finding orders", done: "Found orders" },
    emptyState: { title: "No orders found", description: "No orders matched those filters. Try a wider date range, or drop a filter." },
    coverageCopy: {
      bounded: "Showing the first {shown} of {total} matching {population}.",
      partial: "We could only load some of the matching orders. The ones shown are correct.",
    },
  },
  get_order_detail: {
    capabilityKey: "get_order_detail",
    datasetKey: "order-detail",
    preferredMode: "record",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "orderNo",
          "counterparty",
          "status",
          "orderType",
          "totalAmount",
          "businessDate",
          "shipOrDueDate",
          "lineCoverage",
          "shippingInstructions",
        ],
      },
    },
    // The card header answers "which order"; the lines answer "what is on it". Publishing the line
    // columns is what makes "12 of 12 lines" show 12 lines instead of an empty card.
    recordLines: {
      title: "Order lines and costs",
      rowKindKey: "rowKind",
      headerRowKind: "order_header",
      fieldKeys: ["product", "description", "quantity", "uom", "unitAmount", "extendedAmount", "lifecycleEvidence"],
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "orderNo",
      "counterparty",
      "status",
      "orderType",
      "totalAmount",
      "businessDate",
      "shipOrDueDate",
      "lineCoverage",
      "shippingInstructions",
      "freight",
      "linkedOrderNo",
      "updatedAt",
      "rowKind",
      "product",
      "description",
      "quantity",
      "uom",
      "unitAmount",
      "extendedAmount",
      "lifecycleEvidence",
    ],
    allowedSortFieldKeys: [],
    identityLabels: ["Order"],
    fieldWidths: {},
    materialWarningCategories: ["bounded_order_lines"],
    activity: { running: "Loading order detail", done: "Loaded order detail" },
    emptyState: { title: "Order unavailable", description: "That order doesn't exist, or you don't have access to it." },
    coverageCopy: {
      bounded: "Showing the lines available for this order.",
      partial: "Some of this order's line detail is unavailable right now.",
    },
  },
  get_invoice_detail: {
    capabilityKey: "get_invoice_detail",
    datasetKey: "invoice-detail",
    preferredMode: "record",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "invoiceNo",
          "counterpartyName",
          "status",
          "totalAmount",
          "paidAmount",
          "openAmount",
          "dueDate",
          "paidDate",
          "terms",
          "openingBalance",
        ],
      },
    },
    allowedFieldKeys: [
      "invoiceNo",
      "status",
      "totalAmount",
      "paidAmount",
      "openAmount",
      "counterpartyName",
      "counterpartyStatus",
      "paidDate",
      "voidedAt",
      "openingBalance",
      "terms",
      "submittedDate",
      "dueDate",
      "postedAt",
      "updatedAt",
      "createdBy",
      "createdAt",
      "createdById",
    ],
    allowedSortFieldKeys: [],
    identityLabels: ["Invoice"],
    fieldWidths: {},
    materialWarningCategories: ["incomplete_source"],
    activity: { running: "Loading invoice detail", done: "Loaded invoice detail" },
    emptyState: { title: "Invoice unavailable", description: "That invoice doesn't exist, or you don't have access to it." },
    coverageCopy: { bounded: "Showing the requested invoice.", partial: "Some invoice detail is unavailable right now." },
  },
  get_bill_detail: {
    capabilityKey: "get_bill_detail",
    datasetKey: "bill-detail",
    preferredMode: "record",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "billNo",
          "counterpartyName",
          "status",
          "totalAmount",
          "paidAmount",
          "openAmount",
          "dueDate",
          "paidDate",
          "terms",
          "openingBalance",
        ],
      },
    },
    allowedFieldKeys: [
      "billNo",
      "status",
      "totalAmount",
      "paidAmount",
      "openAmount",
      "counterpartyName",
      "counterpartyStatus",
      "paidDate",
      "voidedAt",
      "openingBalance",
      "terms",
      "issuedDate",
      "dueDate",
      "postedAt",
      "updatedAt",
      "createdBy",
      "createdAt",
      "createdById",
    ],
    allowedSortFieldKeys: [],
    identityLabels: ["Bill"],
    fieldWidths: {},
    materialWarningCategories: ["incomplete_source"],
    activity: { running: "Loading bill detail", done: "Loaded bill detail" },
    emptyState: { title: "Bill unavailable", description: "That bill doesn't exist, or you don't have access to it." },
    coverageCopy: { bounded: "Showing the requested bill.", partial: "Some bill detail is unavailable right now." },
  },
  find_purchase_execution: {
    capabilityKey: "find_purchase_execution",
    datasetKey: "purchase-execution",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Purchase Execution" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "orderNo",
          "supplier",
          "purchaseOrderStatus",
          "warehouse",
          "product",
          "packaging",
          "quantityBasis",
          "expectedQuantity",
          "receivedQuantity",
          "remainingQuantity",
          "dueAt",
          "receiptState",
        ],
      },
    },
    allowedFieldKeys: [
      "orderNo",
      "purchaseOrderId",
      "purchaseOrderItemId",
      "supplier",
      "warehouse",
      "product",
      "productFamily",
      "dueAt",
      "purchaseOrderStatus",
      "purchaseOrderType",
      "packaging",
      "packagingType",
      "quantityBasis",
      "receiptState",
      "expectedQuantity",
      "receivedQuantity",
      "remainingQuantity",
      "overReceivedQuantity",
      "uom",
      "receiptEventCount",
      "evidenceQuality",
      "updatedAt",
    ],
    allowedSortFieldKeys: ["dueAt", "orderNo", "supplier", "product", "receiptState"],
    identityLabels: ["Purchase order line"],
    fieldWidths: { orderNo: { minWidth: 140, maxWidth: 220 }, supplier: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: ["quantity_basis_unknown", "current_state_traversal"],
    activity: { running: "Finding purchase execution", done: "Found purchase execution" },
    emptyState: { title: "No open purchase work", description: "No authorized purchase-order lines matched these filters." },
    coverageCopy: reportCoverageCopy,
  },
  find_inventory_balances: {
    capabilityKey: "find_inventory_balances",
    datasetKey: "inventory_balances",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Inventory Balances" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: ["product", "productFamily", "warehouse", "basis", "onHand", "allocated", "available", "quarantinedOnHand", "archived"],
      },
    },
    allowedFieldKeys: [
      "product",
      "productFamily",
      "warehouse",
      "basis",
      "onHand",
      "allocated",
      "available",
      "quarantinedOnHand",
      "archived",
    ],
    allowedSortFieldKeys: ["product", "productFamily", "warehouse", "onHand", "allocated", "available", "quarantinedOnHand"],
    identityLabels: ["Product", "Warehouse"],
    fieldWidths: {
      product: { minWidth: 180, maxWidth: 320 },
      warehouse: { minWidth: 180, maxWidth: 320 },
    },
    materialWarningCategories: ["current_state_traversal"],
    activity: { running: "Finding inventory balances", done: "Found inventory balances" },
    emptyState: {
      title: "No balances found",
      description: "No physical inventory balance pairs matched those filters.",
    },
    coverageCopy: {
      bounded:
        "Counts cover participating physical product/warehouse pairs before availability filtering; lot receipt-age is not included.",
      partial:
        "Some participating physical-inventory balance evidence is unavailable; do not infer a full-population count or lot receipt-age.",
    },
  },
  find_inventory_pressure: {
    capabilityKey: "find_inventory_pressure",
    datasetKey: "inventory-pressure",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Inventory Pressure" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "product",
          "warehouse",
          "basis",
          "usableOnHand",
          "usableAllocated",
          "unallocatedConfirmedDemand",
          "availableAfterCommitments",
          "draftDemand",
          "projectedAfterDrafts",
          "quarantinedOnHand",
          "quarantinedAllocated",
          "status",
        ],
      },
    },
    allowedFieldKeys: [
      "product",
      "warehouse",
      "basis",
      "usableOnHand",
      "usableAllocated",
      "unallocatedConfirmedDemand",
      "availableAfterCommitments",
      "draftDemand",
      "projectedAfterDrafts",
      "quarantinedOnHand",
      "quarantinedAllocated",
      "status",
    ],
    allowedSortFieldKeys: [
      "product",
      "warehouse",
      "usableOnHand",
      "unallocatedConfirmedDemand",
      "availableAfterCommitments",
      "draftDemand",
      "projectedAfterDrafts",
    ],
    identityLabels: ["Product", "Warehouse"],
    fieldWidths: {
      product: { minWidth: 180, maxWidth: 320 },
      warehouse: { minWidth: 180, maxWidth: 320 },
    },
    materialWarningCategories: ["current_state_traversal"],
    activity: { running: "Checking inventory pressure", done: "Checked inventory pressure" },
    emptyState: {
      title: "No inventory pressure rows",
      description: "No product and warehouse pairs were requested.",
    },
    coverageCopy: {
      bounded:
        "Figures cover only requested pools; quarantine is separate, draft demand is potential, and lot receipt-age is not included.",
      partial: "Some requested pools are withheld and must not be read as zero; lot receipt-age is not included.",
    },
  },
  find_lots: {
    capabilityKey: "find_lots",
    datasetKey: "lots",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Lots" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "lotNumber",
          "product",
          "addedOn",
          "addedQuantity",
          "addedVia",
          "warehouse",
          "lotState",
          "qtyOnHand",
          "basis",
          "manufactureDate",
          "expirationDate",
          "coaAttached",
        ],
      },
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "lotNumber",
      "lotId",
      "product",
      "productId",
      "warehouse",
      "supplier",
      "lotState",
      "packaging",
      "manufactureDate",
      "expirationDate",
      "coaAttached",
      "basis",
      "qtyOnOrder",
      "qtyOnHand",
      "qtyAllocated",
      "qtyAvailable",
      "recordedAddedOn",
      "ageStatus",
      "quarantined",
      "addedOn",
      "addedQuantity",
      "addedVia",
    ],
    allowedSortFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "lotNumber",
      "lotId",
      "product",
      "productId",
      "warehouse",
      "supplier",
      "lotState",
      "packaging",
      "manufactureDate",
      "expirationDate",
      "coaAttached",
      "basis",
      "qtyOnOrder",
      "qtyOnHand",
      "qtyAllocated",
      "qtyAvailable",
      "recordedAddedOn",
      "ageStatus",
      "quarantined",
      "addedOn",
      "addedQuantity",
      "addedVia",
    ],
    fieldCompanions: [{ whenFieldKey: "manufactureDate", companionFieldKeys: ["expirationDate", "coaAttached"] }],
    identityLabels: ["Lot", "Product", "Warehouse"],
    fieldWidths: {
      lotNumber: { minWidth: 160, maxWidth: 280 },
      product: { minWidth: 180, maxWidth: 320 },
      warehouse: { minWidth: 180, maxWidth: 320 },
    },
    materialWarningCategories: ["current_state_traversal", "partial_evidence"],
    activity: { running: "Finding lots", done: "Found lots" },
    emptyState: { title: "No lots found", description: "No authorized lots matched those filters." },
    coverageCopy: {
      bounded: "Lot rows are bounded; age and unresolved coverage use the complete matching source population.",
      partial: "Some lot age evidence is unresolved; do not infer missing dates are recent inventory.",
    },
  },
  get_lot_detail: {
    capabilityKey: "get_lot_detail",
    datasetKey: "lot-detail",
    preferredMode: "record",
    titlePolicy: { kind: "fixed", title: "Lot Detail" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: ["rowKind", "lotNumber", "product", "warehouse", "quantity", "basis", "recordedAddedOn", "sourceKind"],
      },
    },
    recordLines: {
      title: "Remaining Inventory Additions",
      rowKindKey: "rowKind",
      headerRowKind: "lot_header",
      fieldKeys: ["recordedAddedOn", "quantity", "basis", "sourceKind"],
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "rowKind",
      "lotNumber",
      "product",
      "productId",
      "warehouse",
      "quantity",
      "basis",
      "recordedAddedOn",
      "sourceKind",
    ],
    allowedSortFieldKeys: ["recordedAddedOn"],
    identityLabels: ["Lot"],
    fieldWidths: { lotNumber: { minWidth: 160, maxWidth: 280 }, product: { minWidth: 180, maxWidth: 320 } },
    materialWarningCategories: ["current_state_traversal", "partial_evidence"],
    activity: { running: "Reading lot detail", done: "Read lot detail" },
    emptyState: { title: "Lot not found", description: "The requested lot is unavailable." },
    coverageCopy: {
      bounded: "Remaining-layer display is bounded; the oldest remaining addition was computed from the complete graph.",
      partial: "Some lot age evidence is unresolved; do not infer a missing date is recent inventory.",
    },
  },
  find_lot_movements: {
    capabilityKey: "find_lot_movements",
    datasetKey: "lot-movements",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Lot movements" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: ["occurredOn", "lotNumber", "product", "warehouse", "movement", "document", "party", "quantity", "basis", "balance"],
      },
    },
    allowedFieldKeys: [
      "lotId",
      "occurredOn",
      "lotNumber",
      "product",
      "warehouse",
      "movement",
      "document",
      "party",
      "quantity",
      "basis",
      "balance",
    ],
    allowedSortFieldKeys: ["occurredOn", "lotNumber"],
    identityLabels: ["Lot", "Product", "Warehouse"],
    fieldWidths: {
      lotNumber: { minWidth: 160, maxWidth: 280 },
      product: { minWidth: 180, maxWidth: 320 },
      movement: { minWidth: 180, maxWidth: 360 },
    },
    materialWarningCategories: ["current_state_traversal", "partial_evidence"],
    activity: { running: "Reading lot movements", done: "Read lot movements" },
    emptyState: { title: "No lot movements", description: "No lot movements match these filters." },
    coverageCopy: {
      bounded: "Showing a bounded set of lot movements; totals still cover the full population.",
      partial: "Some lot history does not reconcile; the gaps are stated and the rows stay.",
    },
  },
  find_reorder_status: {
    capabilityKey: "find_reorder_status",
    datasetKey: "reorder-status",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Reorder Status" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "product",
          "warehouse",
          "basis",
          "reorderPoint",
          "onHand",
          "allocated",
          "committed",
          "projectedAvailable",
          "projectedDeficit",
          "status",
        ],
      },
    },
    allowedFieldKeys: [
      "product",
      "warehouse",
      "basis",
      "reorderPoint",
      "onHand",
      "allocated",
      "committed",
      "projectedAvailable",
      "projectedDeficit",
      "status",
    ],
    allowedSortFieldKeys: [
      "product",
      "warehouse",
      "reorderPoint",
      "onHand",
      "committed",
      "projectedAvailable",
      "projectedDeficit",
      "status",
    ],
    identityLabels: ["Product", "Warehouse"],
    fieldWidths: {
      product: { minWidth: 180, maxWidth: 320 },
      warehouse: { minWidth: 180, maxWidth: 320 },
    },
    materialWarningCategories: ["current_state_traversal"],
    activity: { running: "Checking reorder status", done: "Checked reorder status" },
    emptyState: {
      title: "No reorder status rows",
      description: "No configured reorder points matched those filters.",
    },
    coverageCopy: {
      bounded:
        "Rows are bounded; certified status counts cover configured points. " +
        "Only configured products are assessed; inbound and on-order are excluded.",
      partial: "Some configured reorder-point evidence is unavailable.",
    },
  },
  find_reorder_with_inbound: {
    capabilityKey: "find_reorder_with_inbound",
    datasetKey: "reorder-with-inbound",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Reorder with inbound" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "product",
          "warehouse",
          "basis",
          "reorderPoint",
          "onHand",
          "allocated",
          "committed",
          "projectedAvailable",
          "projectedDeficit",
          "status",
          "confirmedRemaining",
          "draftPlanned",
        ],
      },
    },
    allowedFieldKeys: [
      "product",
      "warehouse",
      "basis",
      "reorderPoint",
      "onHand",
      "allocated",
      "committed",
      "projectedAvailable",
      "projectedDeficit",
      "status",
      "inboundAvailability",
      "confirmedRemaining",
      "draftPlanned",
      "overdueConfirmed",
      "earliestConfirmedDueAt",
      "inboundLines",
    ],
    allowedSortFieldKeys: [
      "product",
      "warehouse",
      "reorderPoint",
      "onHand",
      "projectedAvailable",
      "projectedDeficit",
      "status",
      "confirmedRemaining",
      "draftPlanned",
      "earliestConfirmedDueAt",
    ],
    identityLabels: ["Product", "Warehouse"],
    fieldWidths: {
      product: { minWidth: 180, maxWidth: 320 },
      warehouse: { minWidth: 180, maxWidth: 320 },
      inboundLines: { minWidth: 240, maxWidth: 480 },
    },
    materialWarningCategories: ["inbound_hidden", "unknown_confirmed_remaining", "warehouse_disagreement"],
    activity: { running: "Checking reorder status and inbound", done: "Checked reorder status and inbound" },
    emptyState: {
      title: "No reorder points configured",
      description: "No active product and warehouse pairs in this scope have reorder points configured.",
    },
    coverageCopy: {
      bounded: "Showing the returned configured product and warehouse rows with inbound kept in separate columns.",
      partial: "Some purchase-order evidence is hidden or unknown; do not infer a zero from a missing inbound value.",
    },
  },
  get_purchase_receipt_events: {
    capabilityKey: "get_purchase_receipt_events",
    datasetKey: "purchase-receipt-events",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Purchase Receipt Events" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: ["lotNumber", "quantity", "receivedAt", "basisEvidence", "evidenceQuality", "createdAt"],
      },
    },
    allowedFieldKeys: [
      "receiptEventId",
      "purchaseOrderItemId",
      "lotNumber",
      "lotNumberEvidence",
      "quantity",
      "receivedAt",
      "productPackagingId",
      "capacityUOM",
      "basisEvidence",
      "evidenceQuality",
      "createdAt",
      "updatedAt",
    ],
    allowedSortFieldKeys: ["receivedAt", "createdAt"],
    identityLabels: ["Receipt event"],
    fieldWidths: { lotNumber: { minWidth: 140, maxWidth: 240 } },
    materialWarningCategories: ["quantity_basis_unknown"],
    activity: { running: "Loading receipt events", done: "Loaded receipt events" },
    emptyState: { title: "No receipt evidence", description: "This purchase-order line has no receipt rows." },
    coverageCopy: reportCoverageCopy,
  },
  list_tags: {
    capabilityKey: "list_tags",
    datasetKey: "tag-vocabulary",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["label", "description", "counts"] } },
    allowedFieldKeys: ["label", "description", "counts"],
    allowedSortFieldKeys: ["label", "counts"],
    identityLabels: ["Tag"],
    fieldWidths: { label: { minWidth: 160, maxWidth: 280 }, description: { minWidth: 200, maxWidth: 420 } },
    materialWarningCategories: [],
    activity: { running: "Listing your tags", done: "Listed your tags" },
    emptyState: { title: "No tags", description: "This organization has no tags in the requested vocabulary." },
    coverageCopy: reportCoverageCopy,
  },
  find_tagged_records: {
    capabilityKey: "find_tagged_records",
    datasetKey: "tagged-records",
    preferredMode: "table",
    titlePolicy: { kind: "model" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["displayName", "entityClass", "externalId"] } },
    allowedFieldKeys: ["displayName", "entityClass", "externalId"],
    allowedSortFieldKeys: ["displayName", "entityClass"],
    identityLabels: ["Record"],
    fieldWidths: { displayName: { minWidth: 180, maxWidth: 320 }, entityClass: { minWidth: 120, maxWidth: 180 } },
    materialWarningCategories: [],
    activity: { running: "Finding tagged records", done: "Found tagged records" },
    emptyState: {
      title: "No tagged records",
      description: [
        "No suppliers, customers, products, or product families carry that resolved team tag.",
        "Order, purchase-order, and opportunity tags are a follow-on search.",
      ].join(" "),
    },
    coverageCopy: reportCoverageCopy,
  },
  find_products: {
    capabilityKey: "find_products",
    datasetKey: "product-directory",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Products by identifier" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "displayName",
          "entityClass",
          "familyName",
          "externalId",
          "casNumber",
          "unNumber",
          "properShippingName",
          "hazardClass",
          "packingGroup",
          "matchBasis",
          "archived",
        ],
      },
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "displayName",
      "entityClass",
      "familyName",
      "externalId",
      "casNumber",
      "unNumber",
      "properShippingName",
      "hazardClass",
      "packingGroup",
      "matchBasis",
      "archived",
    ],
    allowedSortFieldKeys: ["displayName", "entityClass", "familyName", "unNumber", "casNumber", "packingGroup"],
    identityLabels: ["Record"],
    fieldWidths: {
      displayName: { minWidth: 180, maxWidth: 320 },
      unNumber: { minWidth: 100, maxWidth: 160 },
      properShippingName: { minWidth: 200, maxWidth: 420 },
    },
    materialWarningCategories: [],
    activity: { running: "Finding products by identifier", done: "Found products" },
    emptyState: {
      title: "No products",
      description: "No products carry that identifier in the spaces that were searched.",
    },
    coverageCopy: reportCoverageCopy,
  },
  list_certification_types: {
    capabilityKey: "list_certification_types",
    datasetKey: "certification-types",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Certification Types" },
    defaultView: "detail",
    viewPresets: { detail: { fieldKeys: ["title", "description", "subjectType", "enabled", "evidenceRequired"] } },
    allowedFieldKeys: ["title", "description", "subjectType", "enabled", "evidenceRequired", "expiryNoticeDaysBefore"],
    allowedSortFieldKeys: ["title", "subjectType", "enabled"],
    identityLabels: ["Certification type"],
    fieldWidths: {
      title: { minWidth: 180, maxWidth: 320 },
      description: { minWidth: 220, maxWidth: 420 },
    },
    materialWarningCategories: [],
    activity: { running: "Listing certification types", done: "Listed certification types" },
    emptyState: { title: "No certification types", description: "No authorized certification types match that request." },
    coverageCopy: reportCoverageCopy,
  },
  find_certification_records: {
    capabilityKey: "find_certification_records",
    datasetKey: "certification-records",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Certification Records" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "subjectName",
          "subjectType",
          "typeTitle",
          "recordedStatus",
          "effectiveState",
          "validFrom",
          "validTo",
          "evidenceRecordedState",
          "evidenceFileName",
          "recordCompleteness",
          "matchBasis",
        ],
      },
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "subjectName",
      "subjectType",
      "typeTitle",
      "recordedStatus",
      "effectiveState",
      "renewalState",
      "daysLeft",
      "expiryNoticeDaysBefore",
      "validFrom",
      "validTo",
      "evidenceRecordedState",
      "evidenceFileName",
      "recordCompleteness",
      "matchBasis",
    ],
    allowedSortFieldKeys: ["subjectName", "subjectType", "typeTitle", "recordedStatus", "effectiveState", "validTo"],
    identityLabels: ["Certification record"],
    fieldWidths: {
      subjectName: { minWidth: 180, maxWidth: 360 },
      typeTitle: { minWidth: 180, maxWidth: 320 },
      evidenceFileName: { minWidth: 220, maxWidth: 460 },
    },
    materialWarningCategories: [],
    activity: { running: "Finding certification records", done: "Found certification records" },
    emptyState: {
      title: "No certification records",
      description: "No authorized structured certification records match those filters.",
    },
    coverageCopy: reportCoverageCopy,
  },
  find_certification_subjects: {
    capabilityKey: "find_certification_subjects",
    datasetKey: "certification-subjects",
    preferredMode: "table",
    titlePolicy: { kind: "fixed", title: "Subjects with Matching Certification Records" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "subjectName",
          "subjectType",
          "externalId",
          "matchingRecordCount",
          "currentMatchingRecordCount",
          "latestCurrentValidTo",
          "archived",
        ],
      },
    },
    allowedFieldKeys: [
      "subjectName",
      "subjectType",
      "externalId",
      "matchingRecordCount",
      "currentMatchingRecordCount",
      "latestCurrentValidTo",
      "archived",
    ],
    allowedSortFieldKeys: ["subjectName", "subjectType", "matchingRecordCount", "latestCurrentValidTo"],
    identityLabels: ["Subject"],
    fieldWidths: { subjectName: { minWidth: 180, maxWidth: 360 } },
    materialWarningCategories: [],
    activity: { running: "Finding subjects with certification records", done: "Found certification subjects" },
    emptyState: {
      title: "No matching subjects",
      description: "No authorized subjects have matching structured certification records.",
    },
    coverageCopy: reportCoverageCopy,
  },
  get_certification_record: {
    capabilityKey: "get_certification_record",
    datasetKey: "certification-record-detail",
    preferredMode: "record",
    titlePolicy: { kind: "fixed", title: "Certification Record" },
    defaultView: "detail",
    viewPresets: {
      detail: {
        fieldKeys: [
          "subjectName",
          "subjectType",
          "typeTitle",
          "recordedStatus",
          "effectiveState",
          "validFrom",
          "validTo",
          "evidenceRecordedState",
          "evidenceFileName",
          "recordCompleteness",
        ],
      },
    },
    allowedFieldKeys: [
      "createdBy",
      "createdAt",
      "createdById",
      "subjectName",
      "subjectType",
      "typeTitle",
      "recordedStatus",
      "effectiveState",
      "renewalState",
      "daysLeft",
      "expiryNoticeDaysBefore",
      "validFrom",
      "validTo",
      "evidenceRecordedState",
      "evidenceFileName",
      "recordCompleteness",
    ],
    allowedSortFieldKeys: [],
    identityLabels: ["Certification record"],
    fieldWidths: {
      subjectName: { minWidth: 180, maxWidth: 360 },
      typeTitle: { minWidth: 180, maxWidth: 320 },
      evidenceFileName: { minWidth: 220, maxWidth: 460 },
    },
    materialWarningCategories: [],
    activity: { running: "Loading certification record", done: "Loaded certification record" },
    emptyState: { title: "Certification record unavailable", description: "That certification record is not available." },
    coverageCopy: reportCoverageCopy,
  },
};

for (const manifest of Object.values(PRESENTATION_MANIFESTS_BY_CAPABILITY)) {
  GovernedInsightPresentationManifestSchema.parse(manifest);
}

const presentationForContract = (contract: GovernedCapabilityContract): GovernedInsightPresentationManifest => {
  if (contract.capabilityClass !== "report") {
    throw new Error(`Capability ${contract.capabilityKey} does not define a report presentation.`);
  }
  const manifest = Object.values(PRESENTATION_MANIFESTS_BY_CAPABILITY).find(
    candidate => candidate.capabilityKey === contract.capabilityKey,
  );
  if (!manifest || manifest.datasetKey !== contract.datasetKey) {
    throw new Error(`Capability ${contract.capabilityKey} has an incomplete presentation contract.`);
  }
  return manifest;
};

const reportContracts = GOVERNED_CAPABILITY_CONTRACTS.filter(contract => contract.capabilityClass === "report");
if (reportContracts.length !== Object.values(PRESENTATION_MANIFESTS_BY_CAPABILITY).length) {
  throw new Error("Governed report presentation contracts are incomplete.");
}

reportContracts.forEach(presentationForContract);

export const GOVERNED_INSIGHT_PRESENTATION_MANIFESTS = PRESENTATION_MANIFESTS_BY_CAPABILITY;

export const governedInsightPresentationManifest = (capabilityKey: GovernedInsightReportCapabilityKey) =>
  GOVERNED_INSIGHT_PRESENTATION_MANIFESTS[capabilityKey];

export const governedInsightPresentationManifestForDataset = (datasetKey: string): GovernedInsightPresentationManifest | undefined =>
  Object.values(GOVERNED_INSIGHT_PRESENTATION_MANIFESTS).find(manifest => manifest.datasetKey === datasetKey);

/** Reads only declared population paths; callers must establish that the population is complete. */
export const declaredPopulationCurrencies = (
  output: unknown,
  lane: NonNullable<GovernedInsightPresentationManifest["currencyLane"]>,
): string[] | undefined => {
  const atPath = (value: unknown, path: readonly string[]): unknown[] => {
    if (Array.isArray(value)) {
      return value.flatMap(entry => atPath(entry, path));
    }
    const [key, ...remaining] = path;
    if (key === undefined) {
      return [value];
    }
    return typeof value === "object" && value !== null ? atPath(Reflect.get(value, key), remaining) : [];
  };
  for (const path of lane.populationCurrencyPaths) {
    const values = atPath(output, path);
    const currencies = values.filter((value): value is string => typeof value === "string" && /^[A-Z]{3}$/.test(value));
    if (currencies.length > 0 && currencies.length === values.length) {
      return [...new Set(currencies)].sort();
    }
  }
  return undefined;
};
